import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { CustomerLoanStatus, HorizontalListData, HorizontalListFactory } from 'common';
import { LoanData, ProductCode } from '../../loan/domain/models/loan.model';

@Injectable()
export class AccountSummaryAdapter {
  constructor(
    private readonly currencyPipe: CurrencyPipe,
    private readonly datePipe: DatePipe,
    private readonly percentPipe: PercentPipe,
    private readonly decimalPipe: DecimalPipe,
  ) {}

  adaptListData = (loan: LoanData): HorizontalListData[] => {
    return [
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Status',
        customTemplateName: 'statusValue',
        isItemHidden: !this.isStatusVisible(loan),
      }),
      HorizontalListFactory.createCustomTemplateItem({
        customTemplateName: 'payoffNotes',
        isItemHidden: loan?.customerFacingStatus !== CustomerLoanStatus.PaymentDue,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Case number',
        displayedValue: loan?.externalId,
        isItemHidden: loan?.productCode !== ProductCode.Law,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Level policy number',
        displayedValue: loan?.policyNumber,
        isItemHidden: loan?.productCode !== ProductCode.Law,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Level policy limit',
        displayedValue: this.formatCurrency(loan?.policyLimit),
        isItemHidden: loan?.productCode !== ProductCode.Law,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: this.getBalanceTitle(loan?.productCode),
        displayedValue: this.formatCurrency(loan?.loanInfo?.principalBalance),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: this.getAmountTitle(loan?.productCode),
        displayedValue: this.formatCurrency(loan?.loanInfo?.creditLimit),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Interest',
        displayedValue: this.formatCurrency(loan?.loanInfo?.totalInterestCharged),
        isItemHidden: loan?.productCode !== ProductCode.Law,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Next payment',
        displayedValue: this.formatCurrency(loan?.loanInfo?.upcomingPayment),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Payment due date',
        displayedValue: this.getPaymentDueDateValue(loan),
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Daily interest rate',
        displayedValue: this.getDailyInterestRateValue(loan),
        isItemHidden: loan?.productCode !== ProductCode.LOC,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Factor rate',
        displayedValue: this.getFactorRate(loan),
        isItemHidden: loan?.productCode !== ProductCode.Term,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Renewal due date',
        displayedValue: this.formatDate(loan?.renewalDate),
        isItemHidden: !loan?.renewalDate,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Repayment term',
        displayedValue: `${loan?.offer?.repaymentTerm} months`,
        isItemHidden: !this.isRepaymentTermVisible(loan),
      }),
    ];
  };

  private formatCurrency(value: number): string {
    return this.currencyPipe.transform(value, '$');
  }

  private formatDate(value: Date): string {
    return this.datePipe.transform(value, 'mediumDate');
  }

  private getBalanceTitle(productCode: string): string {
    return productCode === ProductCode.Law ? 'Case loan balance' : 'Outstanding balance';
  }

  private getAmountTitle(productCode: string): string {
    return productCode === ProductCode.LOC || productCode === ProductCode.Law ? 'Credit limit' : 'Loan amount';
  }

  private getPaymentDueDateValue(loan: LoanData): string {
    if (loan?.loanInfo?.upcomingPayment > 0) {
      return this.formatDate(loan?.loanInfo?.paymentDueDate);
    }
    return 'None';
  }

  private getDailyInterestRateValue(loan: LoanData): string {
    const year = new Date().getFullYear();
    const daysInCurrentYear = (year % 4 === 0 && year % 100 > 0) || year % 400 == 0 ? 366 : 365;
    const rate = loan?.offer?.interestRate / daysInCurrentYear / 100;
    return this.percentPipe.transform(rate, '1.4-4');
  }

  private getFactorRate(loan: LoanData): string {
    const rate = loan?.offer?.interestRate / 100 + 1;
    return this.decimalPipe.transform(rate, '1.4-4');
  }

  private isStatusVisible(loan: LoanData): boolean {
    return (
      loan?.customerFacingStatus !== CustomerLoanStatus.Open &&
      (loan?.productCode !== ProductCode.Term || loan?.customerFacingStatus !== CustomerLoanStatus.Repayment)
    );
  }

  private isRepaymentTermVisible(loan: LoanData): boolean {
    return (
      loan?.productCode !== ProductCode.Law ||
      loan?.customerFacingStatus === CustomerLoanStatus.Repayment ||
      loan?.customerFacingStatus === CustomerLoanStatus.PaymentDue
    );
  }
}
